import { useEffect, useRef } from 'react';
import { pollTelegramBotLogin } from '../lib/auth-client';
import { failureEvent, failureReason, useAuth } from '../lib/auth-context';
import { track } from '../lib/funnel';
import {
  claimBotLoginWait,
  clearPendingBotLogin,
  onBotLoginWaitReleased,
  readAnyPendingBotLogin,
  releaseBotLoginWait,
  type BotLoginWaitOwner,
  type PendingBotLogin,
} from '../lib/telegram-bot-login';
import { runBotLoginWait } from '../lib/telegram-bot-login-wait';

/** The poll made from here — `via: cookie` on `tg_login_completed`. */
const pollResumed = (token: string) => pollTelegramBotLogin(token, { resumed: true });

/**
 * Collects a «Войти через Telegram» sign-in that the surface which asked for it
 * is no longer mounted to collect. Mounted ONCE at the app root, inside
 * `AuthProvider`. Renders nothing.
 *
 * Since 2026-10-18 Start in the bot IS the sign-in, and the tapped request sits
 * in a short-lived first-party COOKIE rather than this tab's sessionStorage
 * (owner: «tapped, closed the tab, pressed Start, opened the site again in the
 * same browser» must end signed in). So on ANY page load with that cookie this
 * asks the server once, and signs the browser in if Start was pressed.
 *
 * ## The hole this closes (measured 02–09.10.2026)
 *
 * The wait for the bot's confirmation lived inside the tapping surface
 * (`TelegramBotLogin`), and a tapped request was picked back up only by THAT
 * surface. So whenever the surface was gone by the time the person came back
 * from Telegram — a route change, the register page switching to its e-mail
 * step, the onboarding modal on another step, a reload onto a page without
 * that surface (the `t.me` fallback replaces the document) — nobody asked
 * again, and a request confirmed in the bot expired unexchanged.
 *
 * In nginx's log (`/auth/telegram/bot/{start,status}`, 7 days to 09.10): of
 * 188 bot `/start login_…` that produced no account, 21 came from a phone that
 * was back on the site within 30 minutes — a fresh `bot/start` from the same
 * user-agent — with not one `status` poll after the `/start`; and 8 of the 21
 * waits that came back to «bot opened, not confirmed yet» ended in exactly
 * such a fresh `bot/start`, i.e. the waiting surface had unmounted.
 *
 * ## How
 *
 * The tapped request is in the pending cookie (`savePendingBotLogin`),
 * whichever surface tapped. On load, on every return
 * to the tab, and whenever a surface lets go of a wait, this looks for one that
 * nobody is polling (`claimBotLoginWait`) and runs the same loop the surface
 * runs (`runBotLoginWait`). A `signed_in` is adopted exactly as every host
 * does (`completeTelegramBotLogin`); the host's own tail — a redirect, a sent
 * first alert — does not run, because the host is not here.
 *
 * The tapping surface always wins: if it mounts again, this steps aside and
 * the surface's own wait, with its own UI, takes over.
 *
 * Failures are filed under the tapping surface's event and `surface`, with
 * `resumed: true` — rows that did not exist before, because an unmounted
 * surface reported nothing. `consumed` is not a failure here: it means this
 * browser already exchanged the request (a poll that was in flight when the
 * surface unmounted, or another tab), so the session cookie is already set —
 * the session is re-read instead.
 */
export function TelegramBotLoginResume(): null {
  const { completeTelegramBotLogin, refresh, status } = useAuth();
  const completeRef = useRef(completeTelegramBotLogin);
  completeRef.current = completeTelegramBotLogin;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  // Not before the app's first `/auth/me` has answered: that answer was asked
  // without the session an exchange is about to set, and landing after it
  // would put the page back to «guest».
  const authKnown = status !== 'loading';
  const authKnownRef = useRef(authKnown);
  authKnownRef.current = authKnown;
  const checkRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    let current: {
      token: string;
      owner: BotLoginWaitOwner;
      stop: (how?: { keepSignedIn?: boolean }) => void;
    } | null = null;
    let disposed = false;

    const finish = () => {
      if (!current) return;
      const { token, owner, stop } = current;
      current = null;
      stop();
      releaseBotLoginWait(token, owner);
    };

    const report = (p: PendingBotLogin, reason: string) =>
      track(failureEvent(p.intent === 'register' ? 'register' : undefined), {
        method: 'telegram',
        via: 'bot',
        reason,
        surface: p.surface,
        resumed: true,
      });

    const check = () => {
      if (disposed || current || !authKnownRef.current) return;
      const p = readAnyPendingBotLogin();
      if (!p) return;
      const owner: BotLoginWaitOwner = {
        kind: 'resume',
        // The tapping surface mounted again and took the request over. A poll
        // already in flight may be the one that won the exchange — its
        // `signed_in` is still adopted, never dropped.
        yieldTo: () => {
          if (current?.owner !== owner) return;
          current.stop({ keepSignedIn: true });
          current = null;
        },
      };
      if (!claimBotLoginWait(p.token, owner)) return;
      const stop = runBotLoginWait(
        p,
        pollResumed,
        {
          // A new tap replaced it, or its surface finished it: not ours now.
          stillMine: () => {
            if (readAnyPendingBotLogin()?.token === p.token) return true;
            queueMicrotask(finish);
            return false;
          },
          signedIn: (st) => {
            clearPendingBotLogin(p.token);
            finish();
            void Promise.resolve()
              .then(() =>
                completeRef.current({
                  isNew: st.isNew,
                  linked: st.linked,
                  metaEventId: p.metaEventId,
                }),
              )
              .catch(() => undefined);
          },
          refused: (err) => {
            report(p, failureReason(err));
            clearPendingBotLogin(p.token);
            finish();
          },
          ended: (how) => {
            clearPendingBotLogin(p.token);
            finish();
            if (how === 'consumed') {
              void refreshRef.current().catch(() => undefined);
              return;
            }
            report(p, `bot_login_${how}`);
          },
        },
        // Picked up on a load or a return — the person is looking now.
        { firstPollMs: 0 },
      );
      current = { token: p.token, owner, stop };
    };

    // A surface that lets go mid-wait is unmounting — possibly to remount at
    // once (a re-render under a new key, React's dev double effect). Look on
    // the next task, after that remount has had its chance to claim.
    const onReleased = () => {
      window.setTimeout(check, 0);
    };
    const onReturn = () => {
      if (document.visibilityState === 'visible') check();
    };

    checkRef.current = () => {
      window.setTimeout(check, 0);
    };
    const unsubscribe = onBotLoginWaitReleased(onReleased);
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('pageshow', onReturn);
    // Same reason as `onReleased`: the tapping surface, if this page has it,
    // mounts in the same commit and must get the first claim.
    const first = window.setTimeout(check, 0);

    return () => {
      disposed = true;
      checkRef.current = () => undefined;
      window.clearTimeout(first);
      unsubscribe();
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('pageshow', onReturn);
      finish();
    };
  }, []);

  // The first `/auth/me` answered: look now (the mount-time look was too early).
  useEffect(() => {
    if (authKnown) checkRef.current();
  }, [authKnown]);

  return null;
}
